/**
 * 共通レイアウト（画面仕様書 §2.1）。
 * 左サイドバーにロール別のメニュー、上部に画面名と利用者、右側が本文。
 */

import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useGuardedAction } from '../lib/unsaved-context.js';
import { useAuth } from '../auth/context.js';
import { formatMonthLabel, currentMonth } from '../lib/format.js';
import type { Role } from '../api/types.js';

interface MenuItem {
  to: string;
  label: string;
  roles: Role[];
}

const MENU: { group: string; items: MenuItem[] }[] = [
  {
    group: '日報',
    items: [
      { to: '/', label: 'ホーム', roles: ['staff', 'approver', 'admin'] },
      { to: '/daily', label: '日報入力', roles: ['staff', 'approver', 'admin'] },
      { to: '/monthly-entry', label: '月間一覧入力', roles: ['staff', 'approver', 'admin'] },
      { to: '/monthly-list', label: '月間一覧', roles: ['staff', 'approver', 'admin'] },
      { to: '/monthly-summary', label: '月次集計', roles: ['staff', 'approver', 'admin'] },
    ],
  },
  {
    group: '承認',
    items: [
      { to: '/approvals', label: '承認待ち', roles: ['approver', 'admin'] },
      { to: '/approvals/history', label: '承認履歴', roles: ['approver', 'admin'] },
      { to: '/approvals/scope', label: '承認範囲', roles: ['approver', 'admin'] },
    ],
  },
  {
    group: '管理部',
    items: [
      { to: '/admin', label: 'ダッシュボード', roles: ['admin'] },
      { to: '/admin/issues', label: '日報・警告', roles: ['admin'] },
      { to: '/admin/monthly', label: '月次処理', roles: ['admin'] },
      { to: '/admin/calculation', label: '計算根拠', roles: ['admin'] },
      { to: '/admin/groups', label: '承認グループ', roles: ['admin'] },
      { to: '/admin/employees', label: 'スタッフ・権限', roles: ['admin'] },
    ],
  },
];

const ROLE_LABEL: Record<Role, string> = {
  staff: 'スタッフ',
  approver: '承認上長',
  admin: '管理部',
};

export function AppShell({ children }: { children: ReactNode }): React.JSX.Element {
  const { me, signOut } = useAuth();
  const requestAction = useGuardedAction();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const role = me?.employee.role ?? 'staff';

  const groups = MENU.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.roles.includes(role)),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="min-h-full lg:grid lg:grid-cols-[240px_1fr]">
      {navOpen && (
        <button
          type="button"
          aria-label="メニューを閉じる"
          className="fixed inset-0 z-20 bg-ink/30 lg:hidden"
          onClick={() => setNavOpen(false)}
        />
      )}

      <aside
        className={`sidebar fixed inset-y-0 left-0 z-30 w-60 transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          navOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-16 items-center gap-2 px-5">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-white/15 text-sm font-bold">
            S
          </span>
          <div className="leading-tight">
            <p className="text-[0.9rem] font-bold">SEISUI 勤怠</p>
            <p className="text-[0.7rem] text-white/60">日報・給与計算</p>
          </div>
        </div>

        <nav className="space-y-5 p-4">
          {groups.map((group) => (
            <div key={group.group}>
              <p className="sidebar-group mb-1 px-2">{group.group}</p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.to === '/' || item.to === '/admin'}
                      onClick={() => setNavOpen(false)}
                      className={({ isActive }) => `sidebar-link ${isActive ? 'is-active' : ''}`}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex min-h-full flex-col">
        <header className="sticky top-0 z-10 flex h-16 items-center justify-between gap-3 border-b border-line bg-white/95 px-4 backdrop-blur lg:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              className="btn btn--secondary btn--sm lg:hidden"
              onClick={() => setNavOpen(true)}
            >
              メニュー
            </button>
            <div>
              <p className="text-[0.95rem] font-bold">{pageTitle(location.pathname)}</p>
              <p className="text-[0.72rem] text-ink-muted">
                対象月 {formatMonthLabel(me?.currentMonth ?? currentMonth())}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <p className="text-[0.85rem] font-semibold">{me?.employee.name}</p>
              <p className="text-[0.72rem] text-ink-muted">
                {me?.employee.department} ・ {ROLE_LABEL[role]}
              </p>
            </div>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => requestAction(() => void signOut())}
            >
              ログアウト
            </button>
          </div>
        </header>

        <main className="mx-auto w-full max-w-6xl flex-1 space-y-4 p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}

function pageTitle(pathname: string): string {
  const titles: [string, string][] = [
    ['/admin/calculation', '計算根拠'],
    ['/admin/groups', '承認グループ'],
    ['/admin/employees', 'スタッフ・権限'],
    ['/admin/issues', '日報・警告'],
    ['/admin/monthly', '月次処理'],
    ['/admin', '管理ダッシュボード'],
    ['/approvals/history', '承認履歴'],
    ['/approvals/scope', '承認範囲'],
    ['/approvals', '承認待ち'],
    ['/daily', '日報入力'],
    ['/monthly-entry', '月間一覧入力'],
    ['/monthly-list', '月間一覧'],
    ['/monthly-summary', '月次集計'],
  ];
  for (const [prefix, title] of titles) {
    if (pathname.startsWith(prefix)) return title;
  }
  return 'ホーム';
}
